import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedLayout from '@screens/Authenticated/Layout/Layout';
import { Styles } from './styles';
import { usePropertyStore } from '@stores/Property/Property';
import { useMetadataStore } from '@stores/Metadata/Metadata';
import { IProperty } from '@stores/Property/types';
import { CircularProgress, Snackbar, Alert } from '@mui/material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/* ─── Sortable Row ────────────────────────────────────────────── */

interface SortableRowProps {
  property: IProperty;
  index: number;
  isOverLimit: boolean;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onRemove: (id: string) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  property,
  index,
  isOverLimit,
  onView,
  onEdit,
  onRemove
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: property.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Styles.Row $isDragging={isDragging}>
        {isOverLimit && <Styles.RowOverlay />}

        <Styles.DragHandle
          $isDragging={isDragging}
          {...attributes}
          {...listeners}>
          ☰
        </Styles.DragHandle>

        <Styles.OrderBadge>{index + 1}</Styles.OrderBadge>

        {property.foto_capa ? (
          <Styles.Thumbnail src={property.foto_capa} alt={property.titulo} />
        ) : (
          <Styles.ThumbnailEmpty>Sem capa</Styles.ThumbnailEmpty>
        )}

        <Styles.Title>{property.titulo}</Styles.Title>

        <Styles.ButtonsContainer>
          <Styles.ActionButton
            $bg="#2C62C7"
            $hover="#1E4A8E"
            onClick={() => onView(property.id)}>
            Visualizar
          </Styles.ActionButton>
          <Styles.ActionButton
            $bg="#000000"
            $hover="#333333"
            onClick={() => onEdit(property.id)}>
            Editar
          </Styles.ActionButton>
          <Styles.ActionButton
            $bg="#c0392b"
            $hover="#962d22"
            onClick={() => onRemove(property.id)}>
            Remover Destaque
          </Styles.ActionButton>
        </Styles.ButtonsContainer>
      </Styles.Row>
    </div>
  );
};

/* ─── Main Screen ─────────────────────────────────────────────── */

const DestaquesScreen: React.FC = () => {
  const navigate = useNavigate();
  const { fetchAllFeaturedProperties, reordenarDestaques, removerDestaque } =
    usePropertyStore();
  const { configuracoes, fetchConfiguracoes } = useMetadataStore();

  const [properties, setProperties] = useState<IProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const limite = configuracoes?.limite_imoveis_destaque ?? null;

  const loadData = useCallback(async () => {
    setLoading(true);
    const data = await fetchAllFeaturedProperties();
    setProperties(data.sort((a, b) => Number(a.destaque) - Number(b.destaque)));
    setLoading(false);
  }, [fetchAllFeaturedProperties]);

  useEffect(() => {
    loadData();
    fetchConfiguracoes();
  }, [loadData, fetchConfiguracoes]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = properties.findIndex((p) => p.id === active.id);
    const newIndex = properties.findIndex((p) => p.id === over.id);
    const reordered = arrayMove(properties, oldIndex, newIndex);
    setProperties(reordered);

    const ids = reordered.map((p) => Number(p.id));
    const success = await reordenarDestaques(ids);
    if (success) {
      setSnackbar({
        open: true,
        message: 'Ordem dos destaques atualizada!',
        severity: 'success'
      });
    } else {
      // Revert on failure
      setProperties(properties);
      setSnackbar({
        open: true,
        message: 'Erro ao reordenar destaques.',
        severity: 'error'
      });
    }
  };

  const handleRemove = async (id: string) => {
    const success = await removerDestaque(Number(id));
    if (success) {
      setProperties((prev) => prev.filter((p) => p.id !== id));
      setSnackbar({
        open: true,
        message: 'Destaque removido com sucesso.',
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Erro ao remover destaque.',
        severity: 'error'
      });
    }
  };

  const handleView = (id: string) => {
    navigate(`/area-do-corretor/imoveis/consultar/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/area-do-corretor/imoveis/editar/${id}`);
  };

  if (loading) {
    return (
      <AuthenticatedLayout>
        <CircularProgress />
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <h1>Exibindo Imóveis em Destaque</h1>
      <Styles.RectangleBar />

      <Styles.RecordsText>
        Exibindo {properties.length} Registros
      </Styles.RecordsText>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}>
        <SortableContext
          items={properties.map((p) => p.id)}
          strategy={verticalListSortingStrategy}>
          <Styles.ListContainer>
            {properties.map((property, index) => {
              const isOverLimit =
                limite !== null && limite > 0 && index >= limite;
              const isLimitBoundary =
                limite !== null && limite > 0 && index === limite;

              return (
                <React.Fragment key={property.id}>
                  {isLimitBoundary && (
                    <Styles.LimitDivider>
                      <Styles.LimitText>
                        Limite configurado para destaques: {limite}
                      </Styles.LimitText>
                    </Styles.LimitDivider>
                  )}
                  <SortableRow
                    property={property}
                    index={index}
                    isOverLimit={isOverLimit}
                    onView={handleView}
                    onEdit={handleEdit}
                    onRemove={handleRemove}
                  />
                </React.Fragment>
              );
            })}
          </Styles.ListContainer>
        </SortableContext>
      </DndContext>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AuthenticatedLayout>
  );
};

export default DestaquesScreen;
